import { Injectable, OnDestroy } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, switchMap, tap, skip, take } from 'rxjs/operators';

import { LibrariesService } from './libraries-service';
import { QueryParamsUtil } from '../../features/browse-index/utils/query-params.util';
import { LIBRARIES_FILTER_GROUPS_CONFIG, LIBRARIES_SEARCH_DEBOUNCE_MS, LIBRARIES_DEFAULT_PAGE_SIZE } from './constants/libraries.constants';
import { Library } from '../../types/library';
import { BrowseFilter } from '../../features/browse-index/models';

import {
  BrowseIndexError,
  BrowseSearchQuery,
  PaginationState,
  SortState,
  SortField,
  ActiveFilter,
  FilterGroup,
  DEFAULT_SORT,
} from '../../features/browse-index/models';

@Injectable()
export class LibrariesFacade implements OnDestroy {
  private searchSubject$ = new BehaviorSubject<string>('');
  private filtersSubject$ = new BehaviorSubject<Map<string, string[]>>(new Map());
  private pageSubject$ = new BehaviorSubject<number>(1);
  private pageSizeSubject$ = new BehaviorSubject<number>(LIBRARIES_DEFAULT_PAGE_SIZE);
  private sortSubject$ = new BehaviorSubject<SortState>({ ...DEFAULT_SORT });

  private loadingSubject$ = new BehaviorSubject<boolean>(false);
  private errorSubject$ = new BehaviorSubject<BrowseIndexError | null>(null);
  private itemsSubject$ = new BehaviorSubject<Library[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private queryParamsSubject$ = new BehaviorSubject<Params>({});
  private filterGroupsSubject$ = new BehaviorSubject<FilterGroup[]>([]);

  readonly items$: Observable<Library[]> = this.itemsSubject$.asObservable();
  readonly loading$: Observable<boolean> = this.loadingSubject$.asObservable();
  readonly error$: Observable<BrowseIndexError | null> = this.errorSubject$.asObservable();
  readonly searchQuery$: Observable<string> = this.searchSubject$.asObservable();
  readonly sortState$: Observable<SortState> = this.sortSubject$.asObservable();
  readonly filterGroups$: Observable<FilterGroup[]> = this.filterGroupsSubject$.asObservable();

  readonly pagination$: Observable<PaginationState> = combineLatest([
    this.pageSubject$,
    this.pageSizeSubject$,
    this.totalCountSubject$,
  ]).pipe(
    map(([currentPage, pageSize, totalCount]) => ({
      currentPage,
      pageSize,
      totalCount,
      totalPages: Math.ceil(totalCount / pageSize) || 0,
    })),
  );

  readonly activeFilters$: Observable<ActiveFilter[]> = this.filtersSubject$.pipe(
    map(filtersMap => this.buildActiveFilters(filtersMap)),
  );

  readonly isEmpty$: Observable<boolean> = combineLatest([
    this.items$,
    this.loading$,
  ]).pipe(
    map(([items, loading]) => !loading && items.length === 0),
  );

  readonly queryParamsSnapshot$: Observable<Params> = this.queryParamsSubject$.asObservable();

  private processedSearch$: Observable<string> = this.searchSubject$.pipe(
    debounceTime(LIBRARIES_SEARCH_DEBOUNCE_MS),
    map(term => term.trim()),
    distinctUntilChanged(),
    tap(() => this.pageSubject$.next(1)),
  );

  private appliedFilters$ = this.filtersSubject$.pipe(
    map(filtersMap => this.mapToLibraryFilters(filtersMap)),
    tap(() => this.pageSubject$.next(1)),
  );

  private masterTrigger$: Observable<BrowseSearchQuery> = combineLatest([
    this.processedSearch$,
    this.appliedFilters$,
    this.pageSubject$,
    this.pageSizeSubject$,
    this.sortSubject$,
  ]).pipe(
    debounceTime(10),
    map(([search, filters, page, pageSize, sort]) => ({
      search,
      filters,
      page,
      pageSize,
      sort,
    } as BrowseSearchQuery)),
    tap(query => this.queryParamsSubject$.next(QueryParamsUtil.toQueryParams(query))),
  );

  private dataStream$: Observable<void> = this.masterTrigger$.pipe(
    tap(() => {
      this.loadingSubject$.next(true);
      this.errorSubject$.next(null);
    }),
    switchMap(query => {
      this.lastQuery = query;
      return this.librariesService.getLibraries(query).pipe(
        catchError(error => {
          this.errorSubject$.next(error);
          this.loadingSubject$.next(false);
          return of(null);
        }),
      );
    }),
    tap(response => {
      if (response) {
        this.itemsSubject$.next(response.data);
        this.totalCountSubject$.next(response.totalCount);
        this.loadingSubject$.next(false);
      }
    }),
    map(() => void 0),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private dataSubscription: Subscription;
  private lastQuery: BrowseSearchQuery | null = null;

  constructor(private librariesService: LibrariesService) {
    this.dataSubscription = this.dataStream$.subscribe();
    this.initializeFilterGroups();
  }

  ngOnDestroy(): void {
    this.dataSubscription.unsubscribe();
  }

  updateSearch(term: string): void {
    this.searchSubject$.next(term);
  }

  clearSearch(): void {
    this.searchSubject$.next('');
  }

  toggleFilter(groupKey: string, value: string): void {
    const current = new Map(this.filtersSubject$.value);
    const values = [...(current.get(groupKey) ?? [])];
    const index = values.indexOf(value);
    if (index >= 0) {
      values.splice(index, 1);
    } else {
      values.push(value);
    }
    current.set(groupKey, values);
    this.filtersSubject$.next(current);
    this.updateFilterGroupSelections(current);
  }

  clearFilter(groupKey: string): void {
    const current = new Map(this.filtersSubject$.value);
    current.set(groupKey, []);
    this.filtersSubject$.next(current);
    this.updateFilterGroupSelections(current);
  }

  clearAllFilters(): void {
    this.searchSubject$.next('');
    this.filtersSubject$.next(new Map());
    this.updateFilterGroupSelections(new Map());
  }

  resetAll(): void {
    this.clearAllFilters();
    this.sortSubject$.next({ ...DEFAULT_SORT });
    this.pageSubject$.next(1);
    this.pageSizeSubject$.next(LIBRARIES_DEFAULT_PAGE_SIZE);
  }

  goToPage(page: number): void {
    if (page < 1) return;
    this.pageSubject$.next(page);
  }

  changePageSize(size: number): void {
    this.pageSizeSubject$.next(size);
    this.pageSubject$.next(1);
  }

  updateSort(field: SortField): void {
    const current = this.sortSubject$.value;
    if (current.field === field) {
      this.sortSubject$.next({
        field,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      this.sortSubject$.next({
        field,
        direction: 'asc',
      });
    }
    this.pageSubject$.next(1);
  }

  retry(): void {
    this.errorSubject$.next(null);
    this.pageSubject$.next(this.pageSubject$.value);
  }

  restoreStateFromParams(params: Params): void {
    const restored = QueryParamsUtil.fromQueryParams(params);

    if (this.searchSubject$.value !== restored.search) {
      this.searchSubject$.next(restored.search);
    }

    const currentFiltersStr = JSON.stringify(Array.from(this.filtersSubject$.value.entries()));
    const restoredFiltersStr = JSON.stringify(Array.from(restored.filtersMap.entries()));
    if (currentFiltersStr !== restoredFiltersStr) {
      this.filtersSubject$.next(restored.filtersMap);
      this.updateFilterGroupSelections(restored.filtersMap);
    }

    if (JSON.stringify(this.sortSubject$.value) !== JSON.stringify(restored.sort)) {
      this.sortSubject$.next(restored.sort);
    }

    if (this.pageSizeSubject$.value !== restored.pageSize) {
      this.pageSizeSubject$.next(restored.pageSize);
    }

    if (this.pageSubject$.value !== restored.page) {
      this.pageSubject$.next(restored.page);
    }
  }

  private buildActiveFilters(filtersMap: Map<string, string[]>): ActiveFilter[] {
    const activeFilters: ActiveFilter[] = [];
    const filterGroups = this.filterGroupsSubject$.value;

    for (const [groupKey, values] of filtersMap.entries()) {
      const group = filterGroups.find(g => g.key === groupKey);
      if (!group) continue;

      for (const value of values) {
        const option = group.options.find(o => o.value === value);
        activeFilters.push({
          groupKey,
          value,
          label: option ? `${group.label}: ${option.label}` : `${group.label}: ${value}`,
        });
      }
    }
    return activeFilters;
  }

  private initializeFilterGroups(): void {
    const groups: FilterGroup[] = LIBRARIES_FILTER_GROUPS_CONFIG.map(config => ({
      ...config,
      options: [],
      selectedValues: [],
      page: 1,
      hasMore: false,
      loading: false,
      totalCount: 0,
      searchTerm: ''
    }));

    this.filterGroupsSubject$.next(groups);
    groups.forEach(g => this.fetchFacetOptions(g.key, false));
  }

  searchFacet(groupKey: string, term: string): void {
    this.updateFilterGroupState(groupKey, { searchTerm: term, page: 1, allOptions: [] });
    this.fetchFacetOptions(groupKey, false);
  }

  loadMoreFacet(groupKey: string): void {
    const group = this.filterGroupsSubject$.value.find(g => g.key === groupKey);
    if (!group || !group.hasMore || group.loading) return;

    if (group.allOptions) {
      const nextPage = group.page + 1;
      const nextOptions = group.allOptions.slice(0, nextPage * group.pageSize);
      const hasMore = nextOptions.length < group.allOptions.length;

      this.updateFilterGroupState(groupKey, {
        page: nextPage,
        options: nextOptions,
        hasMore
      });
    } else {
      this.updateFilterGroupState(groupKey, { page: group.page + 1 });
      this.fetchFacetOptions(groupKey, true);
    }
  }

  private fetchFacetOptions(groupKey: string, append: boolean): void {
    const group = this.filterGroupsSubject$.value.find(g => g.key === groupKey);
    if (!group) return;

    this.updateFilterGroupState(groupKey, { loading: true });

    this.librariesService.fetchFacetOptions(groupKey, group.searchTerm, group.page, group.pageSize).pipe(
      take(1)
    ).subscribe(result => {
      const currentGroup = this.filterGroupsSubject$.value.find(g => g.key === groupKey)!;

      const allOptions = result.options;
      const visibleOptions = allOptions.slice(0, currentGroup.pageSize);
      const hasMore = visibleOptions.length < allOptions.length;

      this.updateFilterGroupState(groupKey, {
        allOptions,
        options: visibleOptions,
        totalCount: allOptions.length,
        hasMore,
        loading: false
      });
    });
  }

  private updateFilterGroupState(groupKey: string, partialState: Partial<FilterGroup>): void {
    const groups = this.filterGroupsSubject$.value.map(group => {
      if (group.key === groupKey) {
        return { ...group, ...partialState };
      }
      return group;
    });
    this.filterGroupsSubject$.next(groups);
  }

  private updateFilterGroupSelections(filtersMap: Map<string, string[]>): void {
    const groups = this.filterGroupsSubject$.value.map(group => ({
      ...group,
      selectedValues: filtersMap.get(group.key) ?? [],
    }));
    this.filterGroupsSubject$.next(groups);
  }

  /**
   * Build BrowseFilter[] from the filtersMap using LIBRARIES_FILTER_GROUPS_CONFIG.
   * This replaces QueryBuilderUtil.mapToBrowseFilters() which is hardcoded
   * to browse-index FILTER_GROUPS_CONFIG and would never find library filter keys.
   */
  private mapToLibraryFilters(filtersMap: Map<string, string[]>): BrowseFilter[] {
    const filters: BrowseFilter[] = [];

    for (const [groupKey, values] of filtersMap.entries()) {
      if (values.length === 0) continue;

      const config = LIBRARIES_FILTER_GROUPS_CONFIG.find(g => g.key === groupKey);
      if (!config) continue;

      filters.push({
        field: config.field,
        values: [...values],
        operator: values.length > 1 ? 'in' : 'eq',
      });
    }

    return filters;
  }
}
